/* eslint-disable no-useless-return */
import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import FormulaInput from 'components/formula-entry/formula';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import MarkerSearchResults from 'components/formula-entry/marker-search-results';
import { IMarker, IMarkerList } from 'store/formula-entry/types';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CloseIcon,
  Label,
  SaveFormulaButton,
  CancelButton,
  TitleContainer,
  Title,
  ContentSection,
  ContentSectionPanel,
  Container,
  Content,
  InputError,
  InputGroup,
  RemoveBatnaTextButton,
  ScrollableContentSectionPanel,
} from 'components/formula-entry/modals/_common';
import Input from 'components/input/text';
import { isMatch } from 'store/formula-entry/match';
import { validateFormula, triggerDuplicateMarkerPreventionTimer } from 'components/formula-entry/modals/helpers';
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import optionErrors from 'lib/validators/option.errors';
import { TAdditionalBatnasModalProps } from 'components/formula-entry/modals/additional-batnas';
import ButtonOutline from 'components/buttons/outline';
import { ELoadingType } from 'store/loading/types';
import { EDealField, FormulasInterface } from 'store/deals/types';
import MarkerFilters from './marker-filters';
import { IOtherFormulas, TConcatArraysReturnType } from '../types';

const FormulaListContainer = styled.div`
  margin-bottom: 16px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AddFormulaContainer = styled.div`
  margin-top: 32px;
`;

const DefinitionText = styled(RemoveBatnaTextButton)`
  text-align: left;
`;
interface IPriceError {
  name: string;
  formula: string;
  type?: string;
}

const errorsInitialState: IPriceError[] = [];

export interface ICreditProps {
  updateDealField: (field: string, value: IOtherFormulas[]) => void;
}
export type TFormulaListProps = TAdditionalBatnasModalProps & ICreditProps;

const validateActiveFormulaIndex = (showDefinition: boolean, activeFormulaIndex: number, index: number) => {
  return showDefinition && activeFormulaIndex === index;
};

const filterMarkerList = (markerList: IMarkerList[], markerSearchValue: string) => {
  return markerList?.filter((marker: IMarker) => isMatch(markerSearchValue, marker.name));
};

const returnHandleError = (availableMarkers: IMarker[] | null) => {
  if (!availableMarkers) {
    handleError(messages.errors.noMarkers);
  }
};

const getFreshSetOfErrors = (
  formulas: FormulasInterface[] | undefined,
  setOtherFormulas: Function,
  setShowDefinition: Function
) => {
  // initialize appropriate number of error objects
  const freshSetOfErrors: IPriceError[] = [];
  if (formulas && formulas.length > 0) {
    setOtherFormulas(formulas);
    setShowDefinition(false);
    let count = formulas.length;
    while (count > 0) {
      freshSetOfErrors.push({ name: '', formula: '' });
      count -= 1;
    }
  } else {
    freshSetOfErrors.push({ name: '', formula: '' });
  }
  return freshSetOfErrors;
};

// Returns true if any errors are found
function hasErrors(newErrors: IPriceError[]): boolean {
  return newErrors.some(error => error.name || error.formula);
}

const checkPullOptionProductIndex = (productIndex: number | null) => {
  return productIndex === null;
};

const CheckProductIndexValue = (productIndex: number | null) => {
  if (checkPullOptionProductIndex(productIndex)) {
    handleError(messages.errors.default);
  }
};

const processDealAndCheckDescriptionsAndFormula = (
  otherFormulas: IOtherFormulas[],
  newErrors: IPriceError[],
  updateDealField: Function,
  getDealEvaluation: Function,
  cancelFormulaEdit: Function,
  setErrors: Function,
  setLoading: Function
) => {
  const tempNewErrors = newErrors;
  // Run through all descriptions and formulas
  otherFormulas.forEach((otherFormula, index) => {
    if (!otherFormula.name.trim()) {
      tempNewErrors[index].name = optionErrors.requiredField;
    }
    if (!otherFormula.formula.trim()) {
      tempNewErrors[index].formula = optionErrors.requiredField;
    }
  });

  // Update deal, re-evaluate, and close modal if no errors exist
  if (!hasErrors(tempNewErrors)) {
    updateDealField(EDealField.FORMULAS, otherFormulas ?? []);
    getDealEvaluation();
    cancelFormulaEdit();
  } else {
    setErrors(tempNewErrors);
    setLoading(ELoadingType.NONE);
  }
};

function handleInputChange(
  formulaIndex: number,
  field: string,
  value: string,
  otherFormulas: IOtherFormulas[],
  setOtherFormulas: Function
) {
  const otherFormulaUpdated = _.cloneDeep(otherFormulas);
  otherFormulaUpdated[formulaIndex][field] = value;
  setOtherFormulas(otherFormulaUpdated);
}

// Inject marker at cursorPosition
function processInsertMarkers(
  name: string,
  timer: number | null,
  setTimer: Function,
  activeFormulaIndex: number,
  cursorPosition: number,
  otherFormulas: IOtherFormulas[],
  setOtherFormulas: Function
) {
  // Check for active timer, if found, prevent insertion of duplicate marker
  if (timer) {
    return;
  }

  // Kickoff new timer to handle double-clicking of markers
  triggerDuplicateMarkerPreventionTimer(setTimer);

  // Update appropriate batna using index
  const { formula } = otherFormulas[activeFormulaIndex];
  const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
  handleInputChange(activeFormulaIndex, 'formula', formulaValueUpdated, otherFormulas, setOtherFormulas);
}

const checkIsInitializedAndSetErrors = (
  isInitialized: boolean,
  formulas: FormulasInterface[] | undefined,
  setOtherFormulas: Function,
  setShowDefinition: Function,
  setErrors: Function,
  setIsInitialized: Function
) => {
  if (!isInitialized) {
    setErrors(getFreshSetOfErrors(formulas, setOtherFormulas, setShowDefinition));
    setIsInitialized(true);
  }
};

export default ({
  cancelFormulaEdit,
  dealEdit,
  formulaEntry,
  getDealEvaluation,
  setLoading,
  updateDealField,
  allTypesMarkerList,
  updateMarkerList,
}: TFormulaListProps) => {
  const { availableMarkers, productIndex, markerList } = formulaEntry;
  const { formulas } = dealEdit;
  const [isInitialized, setIsInitialized] = useState(false);
  const [errors, setErrors] = useState(errorsInitialState);
  const [otherFormulas, setOtherFormulas] = useState([
    {
      formula: '',
      name: '',
      types: 'custom',
    },
  ]);

  const [activeFormulaIndex, setActiveFormulaIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [filteredMarkers, setFilteredMarkers] = useState([] as TConcatArraysReturnType[]);
  const [markerSearchValue, setMarkerSearchValue] = useState('');
  const [timer, setTimer] = useState(null as number | null);
  const [showDefinition, setShowDefinition] = useState(true);

  useEffect(() => {
    setFilteredMarkers(markerList);
  }, [markerList]);

  // Clears errors
  function flushErrors(errorsToFlush: IPriceError[]): IPriceError[] {
    return errorsToFlush.map(() => ({
      name: '',
      formula: '',
    }));
  }

  // Inject marker at cursorPosition
  function insertMarker(name: string) {
    processInsertMarkers(name, timer, setTimer, activeFormulaIndex, cursorPosition, otherFormulas, setOtherFormulas);
  }

  // Update formulas in product
  async function save() {
    CheckProductIndexValue(productIndex);

    setLoading(ELoadingType.SPINNER);
    const newErrors = flushErrors(errors);

    const promises: Promise<boolean> = new Promise((resolve, reject) => {
      if (otherFormulas) {
        validateFormula(otherFormulas)
          .then(response => {
            const baseError = response.data.error;
            if (baseError) {
              newErrors[otherFormulas.length - 1].formula = capitalizeFirstLetter(baseError.message);
            }
            resolve(true);
          })
          .catch(error => reject(error));
      } else {
        newErrors[0].formula = optionErrors.requiredField;
        resolve(true);
      }
    });

    // Force client-side validation to await resolution of API calls
    await promises.catch(err => handleError(err));

    // Update deal, re-evaluate, and close modal if no errors exist
    processDealAndCheckDescriptionsAndFormula(
      otherFormulas,
      newErrors,
      updateDealField,
      getDealEvaluation,
      cancelFormulaEdit,
      setErrors,
      setLoading
    );
  }

  // Initialize fields
  useEffect(() => {
    checkIsInitializedAndSetErrors(
      isInitialized,
      formulas,
      setOtherFormulas,
      setShowDefinition,
      setErrors,
      setIsInitialized
    );
  }, [isInitialized, formulas]);

  // Manage filtered marker list
  useEffect(() => {
    returnHandleError(availableMarkers);
    const filteredMarkersNew = filterMarkerList(markerList, markerSearchValue);
    setFilteredMarkers(filteredMarkersNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMarkers, markerList, markerSearchValue]);

  // Update marker value and filtered list
  function handleSearchValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    returnHandleError(availableMarkers);
    const val = e.target.value;
    setMarkerSearchValue(val);
  }

  // Handle clear button for marker search input
  function handleSearchValueClear() {
    setMarkerSearchValue('');
    setFilteredMarkers(allTypesMarkerList);
  }

  function handleAddNewFormula() {
    setOtherFormulas([
      ...otherFormulas,
      {
        formula: '',
        name: '',
        types: 'custom',
      },
    ]);
    setErrors([
      ...errors,
      {
        name: '',
        formula: '',
      },
    ]);
    setShowDefinition(true);
    setActiveFormulaIndex(otherFormulas.length);
  }

  function removeFormula(index: number) {
    setOtherFormulas(otherFormulas.filter((_otherFormula, i) => index !== i));
    setErrors(errors.filter((_error, i) => index !== i));
  }

  if (!isInitialized) {
    return <div />;
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Formula List</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <Content>
            <ContentSection>
              <ScrollableContentSectionPanel>
                {otherFormulas ? (
                  otherFormulas.map((otherFormula, index) => (
                    <FormulaListContainer key={`other-formula-${index}`}>
                      <InputGroup>
                        <Label htmlFor={`other-formula-description-input-${index}`} data-valid={!!errors[index].name}>
                          FORMULA {index + 1} LABEL
                        </Label>
                        <Input
                          id={`other-formula-description-input-${index}`}
                          type="text"
                          value={otherFormula.name}
                          onChange={(e: BaseSyntheticEvent) =>
                            handleInputChange(index, 'name', e.target.value, otherFormulas, setOtherFormulas)
                          }
                        />
                        <InputError>{errors[index].name}</InputError>
                      </InputGroup>
                      {validateActiveFormulaIndex(showDefinition, activeFormulaIndex, index) ? (
                        <>
                          <InputGroup>
                            <Label
                              htmlFor={`other-formula-formula-input-${index}`}
                              data-valid={!!errors[index].formula}
                            >
                              FORMULA DEFINITION
                            </Label>
                            <FormulaInput
                              id={`other-formula-formula-input-${index}`}
                              formulaInputValue={otherFormula.formula}
                              isActive={activeFormulaIndex === index}
                              onChange={(e: BaseSyntheticEvent) =>
                                handleInputChange(index, 'formula', e.target.value, otherFormulas, setOtherFormulas)
                              }
                              setActiveFormula={() => setActiveFormulaIndex(index)}
                              setCursorPosition={(p: number) => setCursorPosition(p)}
                              setFormula={(value: string) =>
                                handleInputChange(index, 'formula', value, otherFormulas, setOtherFormulas)
                              }
                            />
                            <InputError>{errors[index].formula}</InputError>
                          </InputGroup>
                          <TextContainer>
                            <DefinitionText type="button" onClick={() => setShowDefinition(false)}>
                              Hide definition
                            </DefinitionText>
                            <RemoveBatnaTextButton type="button" onClick={() => removeFormula(index)}>
                              Remove Formula
                            </RemoveBatnaTextButton>
                          </TextContainer>
                        </>
                      ) : (
                        <DefinitionText
                          type="button"
                          onClick={() => {
                            setShowDefinition(true);
                            setActiveFormulaIndex(index);
                          }}
                        >
                          Show definition
                        </DefinitionText>
                      )}
                    </FormulaListContainer>
                  ))
                ) : (
                  <div>No Markers</div>
                )}
                <AddFormulaContainer>
                  <ButtonOutline data-testid="tid-add-batna-button" onClick={handleAddNewFormula}>
                    Add Formula
                  </ButtonOutline>
                </AddFormulaContainer>
              </ScrollableContentSectionPanel>

              <ContentSectionPanel>
                <Label>SEARCH FOR MARKER</Label>
                <MarkerSearchBox
                  data-testid="mid-marker-search-box"
                  value={markerSearchValue}
                  onChange={handleSearchValueChange}
                  onClear={handleSearchValueClear}
                />
                <MarkerFilters
                  allTypesMarkerList={allTypesMarkerList}
                  updateMarkerList={updateMarkerList}
                  otherFormulas={otherFormulas}
                />
                <MarkerSearchResults
                  markers={filteredMarkers}
                  onClick={insertMarker}
                  data-testid="mid-marker-search-result-item"
                />
              </ContentSectionPanel>
            </ContentSection>
          </Content>

          <ButtonContainer>
            <CancelButton onClick={cancelFormulaEdit}>Cancel</CancelButton>
            <SaveFormulaButton onClick={save}>Apply Formulas</SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
